import React, {useContext, useEffect, useRef, useState} from "react";
import LabShell from "./LabShell";
import StyleContext from "../contexts/StyleContext";

const scenes = [
  {
    id: "satellite",
    label: "Satellite having a normal one",
    shortLabel: "Satellite",
    icon: "🛰️",
    palette: ["#020713", "#28205f", "#5bd8ff"],
    captions: [
      ["GROUND SAID IT'S PROBABLY FINE", "ORBIT DETERMINATION DISAGREES"],
      ["JUST ONE LAST SOFTWARE PATCH", "THE SPACECRAFT, 500 KM AWAY"],
      ["WHEN THE WATCHDOG TIMER FIRES", "AND YOU WERE THE ONE BEING WATCHED"]
    ]
  },
  {
    id: "eva",
    label: "EVA procedure gets creative",
    shortLabel: "EVA",
    icon: "🧑‍🚀",
    palette: ["#10051d", "#4c1b64", "#eaa8ff"],
    captions: [
      ["THE PROCEDURE SAYS USE DISCRETION", "I HAVE SELECTED PANIC"],
      ["PLANNED EVA: 45 MINUTES", "ONE BOLT: ABSOLUTELY NOT"],
      ["ME: I'LL BE RIGHT BACK", "ORBITAL MECHANICS: DEFINE BACK"]
    ]
  },
  {
    id: "rocket",
    label: "Launch day optimism",
    shortLabel: "Launch",
    icon: "🚀",
    palette: ["#18090a", "#983b1d", "#ffbe55"],
    captions: [
      ["COUNTDOWN HOLD AT T-MINUS 10", "SOMEONE LEFT CAPS LOCK ON"],
      ["THE SIMULATION HAD 100% SUCCESS", "THE WEATHER DID NOT READ THE REPORT"],
      ["IT'S NOT A SCRUB", "IT'S AN UNPLANNED LAUNCH REHEARSAL"]
    ]
  },
  {
    id: "rover",
    label: "Rover versus one rock",
    shortLabel: "Mars rover",
    icon: "🤖",
    palette: ["#210c08", "#8c301d", "#ff8652"],
    captions: [
      ["AUTONOMOUS NAVIGATION ENABLED", "ROVER IMMEDIATELY CHOOSES A ROCK"],
      ["DRIVE COMMAND: 10 METERS", "MARS: BEST I CAN DO IS 9.7"],
      ["NEW TERRAIN CLASSIFIER DEPLOYED", "EVERYTHING IS NOW SUSPICIOUS SAND"]
    ]
  },
  {
    id: "control",
    label: "Mission control on Friday",
    shortLabel: "Control room",
    icon: "🧑‍💻",
    palette: ["#03120f", "#07574f", "#52f0c4"],
    captions: [
      ["TELEMETRY LOOKS WEIRD", "HAVE YOU TRIED INVERTING THE Y-AXIS?"],
      ["FRIDAY DEPLOYMENT COMPLETE", "MONDAY ANOMALY REVIEW SCHEDULED"],
      ["ONE SMALL CONFIG CHANGE", "ONE GIANT INCIDENT REPORT"]
    ]
  },
  {
    id: "lunar",
    label: "Lunar landing surprise",
    shortLabel: "Moon landing",
    icon: "🌕",
    palette: ["#080b16", "#334059", "#d8e4ff"],
    captions: [
      ["LANDING SITE: FLAT AND BORING", "LIDAR: I HAVE NEWS"],
      ["WE BROUGHT THREE BACKUP SENSORS", "GUESS WHICH THREE FAILED"],
      ["THE ESTIMATED ETA WAS PRECISE", "ACCURACY WAS NOT INCLUDED"]
    ]
  }
];

const captionStyles = [
  {id: "classic", label: "Classic"},
  {id: "telemetry", label: "Telemetry"},
  {id: "deadpan", label: "Deadpan"}
];

const stamps = ["NONE", "NOMINAL*", "ANOMALY!", "SEND IT"];

const starField = Array.from({length: 64}, (_, index) => ({
  x: (index * 137 + 41) % 960,
  y: (index * 79 + 23) % 640,
  r: index % 8 === 0 ? 2 : 1
}));

function roundedRect(context, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + r, y);
  context.arcTo(x + width, y, x + width, y + height, r);
  context.arcTo(x + width, y + height, x, y + height, r);
  context.arcTo(x, y + height, x, y, r);
  context.arcTo(x, y, x + width, y, r);
  context.closePath();
}

function drawFittedText(context, text, y, maxWidth, color = "#fff") {
  const cleanText = text.trim().toUpperCase() || "ADD A CAPTION";
  let size = 54;
  context.font = `900 ${size}px Montserrat, sans-serif`;
  while (context.measureText(cleanText).width > maxWidth && size > 25) {
    size -= 2;
    context.font = `900 ${size}px Montserrat, sans-serif`;
  }
  context.lineWidth = Math.max(5, size / 8);
  context.strokeStyle = color === "#fff" ? "rgba(1,3,10,.95)" : "rgba(255,255,255,.8)";
  context.textAlign = "center";
  context.strokeText(cleanText, 480, y);
  context.fillStyle = color;
  context.fillText(cleanText, 480, y);
}

function drawScene(context, selected) {
  const gradient = context.createLinearGradient(0, 0, 960, 640);
  gradient.addColorStop(0, selected.palette[0]);
  gradient.addColorStop(.62, selected.palette[1]);
  gradient.addColorStop(1, selected.palette[0]);
  context.fillStyle = gradient;
  context.fillRect(0, 0, 960, 640);

  if (["satellite", "eva", "lunar"].includes(selected.id)) {
    context.fillStyle = "rgba(235,246,255,.78)";
    starField.forEach(star => {
      context.beginPath();
      context.arc(star.x, star.y, star.r, 0, Math.PI * 2);
      context.fill();
    });
  }

  if (selected.id === "satellite") {
    const earthGlow = context.createRadialGradient(790, 610, 20, 790, 610, 260);
    earthGlow.addColorStop(0, "rgba(74,211,238,.9)");
    earthGlow.addColorStop(.5, "rgba(29,101,218,.75)");
    earthGlow.addColorStop(1, "rgba(15,34,91,0)");
    context.fillStyle = earthGlow;
    context.fillRect(500, 320, 460, 320);
    context.fillStyle = "#17488e";
    context.beginPath();
    context.arc(815, 690, 245, 0, Math.PI * 2);
    context.fill();
  }

  if (selected.id === "eva") {
    const nebula = context.createRadialGradient(470, 325, 10, 470, 325, 330);
    nebula.addColorStop(0, "rgba(232,130,255,.34)");
    nebula.addColorStop(.45, "rgba(98,55,211,.2)");
    nebula.addColorStop(1, "rgba(8,4,22,0)");
    context.fillStyle = nebula;
    context.fillRect(80, 30, 780, 580);
    context.fillStyle = "rgba(221,225,241,.22)";
    context.beginPath();
    context.arc(130, 665, 235, 0, Math.PI * 2);
    context.fill();
  }

  if (selected.id === "rocket") {
    context.fillStyle = "rgba(255,190,85,.18)";
    context.beginPath();
    context.arc(480, 650, 390, Math.PI, Math.PI * 2);
    context.fill();
    context.strokeStyle = "rgba(255,226,179,.35)";
    context.lineWidth = 8;
    context.beginPath();
    context.moveTo(260, 535);
    context.lineTo(700, 535);
    context.stroke();
  }

  if (selected.id === "rover") {
    context.fillStyle = "rgba(255,112,68,.18)";
    for (let i = 0; i < 4; i += 1) {
      context.beginPath();
      context.ellipse(160 + i * 250, 565 - (i % 2) * 40, 230, 100, -.08, 0, Math.PI * 2);
      context.fill();
    }
    context.strokeStyle = "rgba(255,190,140,.28)";
    context.lineWidth = 2;
    for (let x = 45; x < 940; x += 95) {
      context.beginPath();
      context.moveTo(x, 520 + (x % 3) * 10);
      context.lineTo(x + 42, 512 + (x % 4) * 8);
      context.stroke();
    }
  }

  if (selected.id === "control") {
    context.strokeStyle = "rgba(82,240,196,.2)";
    context.lineWidth = 2;
    for (let x = 35; x < 960; x += 95) {
      context.beginPath();
      context.moveTo(x, 0);
      context.lineTo(x, 640);
      context.stroke();
    }
    for (let y = 35; y < 640; y += 70) {
      context.beginPath();
      context.moveTo(0, y);
      context.lineTo(960, y);
      context.stroke();
    }
    context.fillStyle = "rgba(1,12,15,.68)";
    roundedRect(context, 90, 145, 780, 350, 26);
    context.fill();
    context.strokeStyle = "rgba(82,240,196,.55)";
    context.stroke();
  }

  if (selected.id === "lunar") {
    context.fillStyle = "rgba(215,225,244,.24)";
    context.beginPath();
    context.arc(480, 750, 390, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = "rgba(88,133,235,.65)";
    context.beginPath();
    context.arc(785, 155, 45, 0, Math.PI * 2);
    context.fill();
  }

  context.save();
  context.translate(480, selected.id === "rocket" ? 360 : 350);
  context.rotate(selected.id === "satellite" ? -.3 : selected.id === "eva" ? .18 : 0);
  context.textAlign = "center";
  context.font = selected.id === "control"
    ? "180px 'Segoe UI Emoji', sans-serif"
    : "190px 'Segoe UI Emoji', sans-serif";
  context.shadowColor = selected.palette[2];
  context.shadowBlur = 34;
  context.fillText(selected.icon, 0, 55);
  context.restore();
  context.shadowBlur = 0;

  context.fillStyle = selected.palette[2];
  context.font = "800 12px Montserrat, sans-serif";
  context.textAlign = "left";
  context.fillText(selected.shortLabel.toUpperCase(), 28, 32);
}

function drawCaptions(context, topText, bottomText, captionStyle) {
  if (captionStyle === "telemetry") {
    context.fillStyle = "rgba(2,5,14,.82)";
    roundedRect(context, 24, 48, 912, 86, 15);
    context.fill();
    roundedRect(context, 24, 506, 912, 86, 15);
    context.fill();
    context.fillStyle = "#7d9dff";
    context.font = "800 10px Montserrat, sans-serif";
    context.textAlign = "left";
    context.fillText("UPLINK", 45, 72);
    context.fillText("DOWNLINK", 45, 530);
    drawFittedText(context, topText, 112, 810);
    drawFittedText(context, bottomText, 571, 810);
    return;
  }

  if (captionStyle === "deadpan") {
    context.fillStyle = "rgba(255,255,255,.94)";
    context.fillRect(0, 0, 960, 104);
    context.fillRect(0, 536, 960, 104);
    drawFittedText(context, topText, 69, 875, "#071124");
    drawFittedText(context, bottomText, 606, 875, "#071124");
    return;
  }

  drawFittedText(context, topText, 78, 875);
  drawFittedText(context, bottomText, 602, 875);
}

function drawStamp(context, stamp) {
  if (stamp === "NONE") return;
  const stampColors = {"NOMINAL*": "#6ff2bd", "ANOMALY!": "#ff605d", "SEND IT": "#ffd15c"};
  context.save();
  context.translate(760, 455);
  context.rotate(-.12);
  context.strokeStyle = stampColors[stamp];
  context.fillStyle = stampColors[stamp];
  context.lineWidth = 5;
  roundedRect(context, -125, -38, 250, 76, 8);
  context.stroke();
  context.font = "900 31px Montserrat, sans-serif";
  context.textAlign = "center";
  context.fillText(stamp, 0, 12);
  context.restore();
}

export default function MemeGenerator() {
  const {isDark} = useContext(StyleContext);
  const canvasRef = useRef(null);
  const [sceneIndex, setSceneIndex] = useState(0);
  const [captionIndex, setCaptionIndex] = useState(0);
  const [topText, setTopText] = useState(scenes[0].captions[0][0]);
  const [bottomText, setBottomText] = useState(scenes[0].captions[0][1]);
  const [captionStyle, setCaptionStyle] = useState("classic");
  const [stamp, setStamp] = useState("NONE");

  const selectedScene = scenes[sceneIndex];

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    drawScene(context, selectedScene);
    drawCaptions(context, topText, bottomText, captionStyle);
    drawStamp(context, stamp);
  }, [selectedScene, topText, bottomText, captionStyle, stamp, isDark]);

  const selectScene = index => {
    setSceneIndex(index);
    setCaptionIndex(0);
    setTopText(scenes[index].captions[0][0]);
    setBottomText(scenes[index].captions[0][1]);
  };

  const newCaption = () => {
    const nextCaption = (captionIndex + 1) % selectedScene.captions.length;
    setCaptionIndex(nextCaption);
    setTopText(selectedScene.captions[nextCaption][0]);
    setBottomText(selectedScene.captions[nextCaption][1]);
  };

  const shuffleMission = () => {
    const nextScene = (sceneIndex + 1 + Math.floor(Math.random() * (scenes.length - 1))) % scenes.length;
    const nextCaption = Math.floor(Math.random() * scenes[nextScene].captions.length);
    const nextStyle = captionStyles[Math.floor(Math.random() * captionStyles.length)].id;
    const nextStamp = stamps[Math.floor(Math.random() * stamps.length)];
    setSceneIndex(nextScene);
    setCaptionIndex(nextCaption);
    setTopText(scenes[nextScene].captions[nextCaption][0]);
    setBottomText(scenes[nextScene].captions[nextCaption][1]);
    setCaptionStyle(nextStyle);
    setStamp(nextStamp);
  };

  const download = () => {
    const link = document.createElement("a");
    link.download = `space-meme-${selectedScene.id}.png`;
    link.href = canvasRef.current.toDataURL("image/png");
    link.click();
  };

  return (
    <LabShell
      eyebrow="MISSION LAB / 02"
      title="Space Meme Generator"
      intro="Pick a space mishap, write the punchline, and send it to the group chat."
    >
      <section className="meme-layout meme-layout-redesign">
        <div className="meme-workbench">
          <div className="meme-canvas-wrap">
            <span className="meme-live-badge">LIVE PREVIEW</span>
            <canvas ref={canvasRef} width="960" height="640" aria-label="Generated space meme preview" />
          </div>
          <div className="meme-underbar">
            <span>18 hand-written aerospace jokes. Zero AI.</span>
            <button onClick={newCaption}>New caption for this scene ↻</button>
          </div>
        </div>

        <aside className="meme-controls">
          <div className="meme-control-head">
            <span>MEME CONSOLE</span>
            <strong>Choose your disaster.</strong>
          </div>

          <div className="control-group">
            <span>SCENARIO</span>
            <div className="scene-options scene-deck">
              {scenes.map((item, index) => (
                <button
                  className={sceneIndex === index ? "is-selected" : ""}
                  key={item.id}
                  onClick={() => selectScene(index)}
                  aria-pressed={sceneIndex === index}
                  title={item.label}
                >
                  <b>{item.icon}</b><span>{item.shortLabel}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="meme-copy-grid">
            <div className="control-group">
              <label htmlFor="top-text">SETUP</label>
              <input id="top-text" maxLength="64" value={topText} onChange={event => setTopText(event.target.value)} />
            </div>
            <div className="control-group">
              <label htmlFor="bottom-text">PUNCHLINE</label>
              <input id="bottom-text" maxLength="64" value={bottomText} onChange={event => setBottomText(event.target.value)} />
            </div>
          </div>

          <div className="control-group compact-control-group">
            <span>CAPTION STYLE</span>
            <div className="meme-chip-row">
              {captionStyles.map(style => (
                <button
                  className={captionStyle === style.id ? "is-selected" : ""}
                  key={style.id}
                  onClick={() => setCaptionStyle(style.id)}
                  aria-pressed={captionStyle === style.id}
                >
                  {style.label}
                </button>
              ))}
            </div>
          </div>

          <div className="control-group compact-control-group">
            <span>STATUS STAMP</span>
            <div className="meme-chip-row stamp-row">
              {stamps.map(item => (
                <button
                  className={stamp === item ? "is-selected" : ""}
                  key={item}
                  onClick={() => setStamp(item)}
                  aria-pressed={stamp === item}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

          <div className="meme-actions">
            <button className="secondary-action" onClick={shuffleMission}>Shuffle mission</button>
            <button className="primary-action" onClick={download}>Download PNG ↗</button>
          </div>
        </aside>
      </section>
    </LabShell>
  );
}
